import { Engine3d } from '../../bridge/Engine3d';
import { Node } from '../Node';
import { Component } from '../component';
import { MeshAssembly } from 'src/bridge/MeshAssembly';

export interface ComponentInnerState {
	meshAssembly?: MeshAssembly;
}

export abstract class Processor {
	constructor(
		protected engine: Engine3d,
		private componentStates: Map<Component, ComponentInnerState>
	) {}
	protected updateSet: Set<Component> = new Set<Component>();
	protected enableSet: Set<Component> = new Set<Component>();
	protected destroySet: Set<Component> = new Set<Component>();

	public debug: boolean;

	public update(component: Component, node: Node): void {
		if (this.updateSet.has(component)) {
			if (component.isDisabled) {
				this.disable(component, node);
				this.updateSet.delete(component);
				this.enableSet.add(component);
			} else {
				const update = this.updateState(component, node);
				if (!update) {
					this.destroy(component, node);
					this.start(component, node);
					const enable = this.enable(component, node);
					if (!enable) {
						this.updateSet.delete(component);
						this.enableSet.add(component);
					} else {
						this.updateSet.add(component);
					}
				}
			}
		} else if (this.enableSet.has(component)) {
			const enable = this.enable(component, node);
			if (enable) {
				this.enableSet.delete(component);
				this.updateSet.add(component);
			}
		} else {
			if (this.destroySet.has(component)) {
				this.destroySet.delete(component);
				return;
			}
			const start = this.start(component, node);
			if (start) {
				const enable = this.enable(component, node);
				if (enable) {
					this.updateSet.add(component);
				} else {
					this.enableSet.add(component);
				}
			}
		}
	}

	public destroy(c: Component, node: Node): void {
		if (this.updateSet.has(c) || this.enableSet.has(c)) {
			this.updateSet.delete(c);
			this.enableSet.delete(c);
			this.onDestroy(c, node);
		} else {
			this.destroySet.add(c);
		}
	}

	protected abstract updateState(c: Component, node: Node): boolean;
	protected abstract start(c: Component, node: Node): boolean;
	protected abstract enable(c: Component, node: Node): boolean;
	protected abstract disable(c: Component, node: Node): boolean;

	protected abstract onDestroy(c: Component, node: Node): boolean;

	protected getState<T extends ComponentInnerState>(component: Component): T {
		return this.componentStates.get(component) as T;
	}

	protected setState(component: Component, state: ComponentInnerState): void {
		this.componentStates.set(component, state);
	}

	protected deleteState(component: Component): boolean {
		return this.componentStates.delete(component);
	}
}
